import type { DocumentSummary } from '../api/types';
import { isPayable, STATUS } from '../lib/doc';
import { dt, money, todayIso } from '../lib/format';
import { Pill } from './ui';

/** Kolik dní před splatností začne odznak upozorňovat (oranžová mince s počtem dní). */
export const DUE_SOON_DAYS = 6;

/** Stavy, kdy doklad ještě čeká na peníze. */
const WAITING = new Set(['unpaid', 'overdue', 'partial', 'sent']);

export type DueTone = 'paid' | 'calm' | 'soon' | 'late';

export interface DueInfo {
  tone: DueTone;
  /** dny do splatnosti (soon) nebo po splatnosti (late); jinak null */
  days: number | null;
  /** celá věta pro tooltip a čtečky */
  text: string;
}

const dayWord = (n: number) => (n === 1 ? 'den' : n >= 2 && n <= 4 ? 'dny' : 'dní');

/** Rozdíl kalendářních dní mezi dvěma ISO daty (bez vlivu časových pásem). */
export function daysBetween(fromIso: string, toIso: string): number {
  const a = Date.UTC(...(fromIso.slice(0, 10).split('-').map(Number) as [number, number, number]));
  const b = Date.UTC(...(toIso.slice(0, 10).split('-').map(Number) as [number, number, number]));
  return Math.round((b - a) / 86_400_000);
}

/**
 * Stav úhrady jedním pohledem: zaplaceno, v klidu, blíží se splatnost, po splatnosti.
 * Vrací null u dokladů, kde splatnost nedává smysl (koncept, storno, dodací list, dobropis).
 */
export function dueInfo(
  doc: Pick<DocumentSummary, 'type' | 'status' | 'dueAt' | 'remaining' | 'currency'>,
  today = todayIso(),
): DueInfo | null {
  if (!isPayable(doc.type) || doc.type === 'DB') return null;
  if (doc.status === 'paid') return { tone: 'paid', days: null, text: 'Zaplaceno' };
  if (!WAITING.has(doc.status)) return null;

  const left = daysBetween(today, doc.dueAt);
  const rest =
    doc.status === 'partial' ? ` · zbývá ${money(doc.remaining, doc.currency ?? 'CZK')}` : '';
  if (left < 0) {
    const late = -left;
    return { tone: 'late', days: late, text: `${late} ${dayWord(late)} po splatnosti${rest}` };
  }
  if (left === 0) return { tone: 'soon', days: 0, text: `Splatné dnes${rest}` };
  if (left <= DUE_SOON_DAYS) {
    return {
      tone: 'soon',
      days: left,
      text: `Splatné za ${left} ${dayWord(left)}, ${dt(doc.dueAt)}${rest}`,
    };
  }
  return { tone: 'calm', days: null, text: `Splatnost ${dt(doc.dueAt)}${rest}` };
}

const coin = (
  <svg viewBox="0 0 20 20" aria-hidden="true" className="coin-i">
    <circle cx="10" cy="10" r="7.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M12.2 7.6c-.4-.6-1.2-1-2.2-1-1.3 0-2.2.7-2.2 1.6 0 2.1 4.6 1 4.6 3.4 0 .9-.9 1.7-2.4 1.7-1 0-1.9-.4-2.3-1.1M10 5.4v1.2m0 6.8v1.2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const check = (
  <svg viewBox="0 0 20 20" aria-hidden="true" className="coin-i">
    <circle cx="10" cy="10" r="7.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="m6.8 10.2 2.2 2.2 4.2-4.6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Odznak stavu dokladu v seznamu. U plateb mince: šedá = v klidu, oranžová s číslem =
 * dní do splatnosti, červená s číslem = dní po splatnosti, zelená fajfka = zaplaceno.
 * U ostatního (koncept, storno, dodací list, dobropis) zůstává textový štítek.
 */
export function DueBadge({ doc }: { doc: DocumentSummary }) {
  const info = dueInfo(doc);
  if (!info) return <Pill kind={doc.status}>{STATUS[doc.status]}</Pill>;
  return (
    <span className={`due due-${info.tone}`} title={info.text}>
      {info.tone === 'paid' ? check : coin}
      {info.days !== null && <b>{info.days}</b>}
      <span className="sr-only">{info.text}</span>
    </span>
  );
}
